/**
 * ReadingCanvas — Two-layer rendering surface.
 *
 * Base canvas: text rendered once, only redrawn on scroll/layout changes.
 * Overlay canvas: lightweight spotlight effect, redrawn on gaze movement.
 *
 * Text pixels are NEVER cleared during gaze movement, eliminating flicker.
 */

import { useRef, useEffect, useCallback, useState } from 'react'
import { useStore } from '@/store'
import { computeLayout, renderBaseLayer, renderOverlay, gazeToLine } from '@/lib/pretext-renderer'
import { gazeBridge } from '@/lib/gaze-bridge'
import { gazeTracker } from '@/lib/gaze-tracker'
import { useSmoothedScroll } from '@/hooks/useSmoothedScroll'

const SCROLL_SPEED = 80
const PAGE_SCROLL = 600
const AUTO_SCROLL_ZONE = 0.15
const AUTO_SCROLL_SPEED = 1.5
const GAZE_Y_OFFSET = -50 // px — webcam tends to track below actual gaze point

export function ReadingCanvas() {
  const baseCanvasRef = useRef<HTMLCanvasElement>(null)
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number | null>(null)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })

  // Track what was last rendered to avoid unnecessary base redraws
  const lastBaseRenderRef = useRef({ scrollY: -1, linesLen: -1, settingsHash: '' })

  const document = useStore((s) => s.document)
  const settings = useStore((s) => s.settings)
  const scrollY = useStore((s) => s.scrollY)
  const setLayout = useStore((s) => s.setLayout)
  const setScrollY = useStore((s) => s.setScrollY)
  const lines = useStore((s) => s.lines)
  const totalHeight = useStore((s) => s.totalHeight)
  const trackingMode = useStore((s) => s.trackingMode)

  // Refs for render loop
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  const scrollYRef = useRef(scrollY)
  scrollYRef.current = scrollY
  const linesRef = useRef(lines)
  linesRef.current = lines
  const trackingModeRef = useRef(trackingMode)
  trackingModeRef.current = trackingMode
  const fixationLineRef = useRef<number | null>(null)

  const getScrollY = useCallback(() => scrollY, [scrollY])
  const getMaxScroll = useCallback(
    () => Math.max(0, totalHeight - canvasSize.height + 100),
    [totalHeight, canvasSize.height],
  )

  const { scrollBy, tick: scrollTick } = useSmoothedScroll(getScrollY, setScrollY, getMaxScroll)

  const scrollTickRef = useRef(scrollTick)
  scrollTickRef.current = scrollTick
  const scrollByRef = useRef(scrollBy)
  scrollByRef.current = scrollBy

  // Track container size
  useEffect(() => {
    const container = baseCanvasRef.current?.parentElement
    if (!container) return

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        if (width > 0 && height > 0) {
          setCanvasSize({ width: Math.round(width), height: Math.round(height) })
        }
      }
    })
    observer.observe(container)

    const rect = container.getBoundingClientRect()
    if (rect.width > 0) {
      setCanvasSize({ width: Math.round(rect.width), height: Math.round(rect.height) })
    }

    return () => observer.disconnect()
  }, [])

  // Sync canvas buffer dimensions when size changes
  useEffect(() => {
    const { width, height } = canvasSize
    if (width === 0 || height === 0) return

    const dpr = window.devicePixelRatio || 1
    const bufW = Math.round(width * dpr)
    const bufH = Math.round(height * dpr)

    for (const ref of [baseCanvasRef, overlayCanvasRef]) {
      const canvas = ref.current
      if (!canvas) continue
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      canvas.width = bufW
      canvas.height = bufH
      const ctx = canvas.getContext('2d')
      if (ctx) ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    // Force base layer redraw
    lastBaseRenderRef.current.scrollY = -1
  }, [canvasSize])

  // Compute layout when document, settings, or canvas width change
  useEffect(() => {
    if (!document || canvasSize.width === 0) return
    const { lines: newLines, totalHeight: newHeight } = computeLayout(
      document.paragraphs,
      settings,
      canvasSize.width,
    )
    setLayout(newLines, newHeight)
  }, [document, settings, canvasSize.width, setLayout])

  // Render loop
  const render = useCallback(() => {
    const baseCanvas = baseCanvasRef.current
    const overlayCanvas = overlayCanvasRef.current
    if (!baseCanvas || !overlayCanvas) return

    const baseCtx = baseCanvas.getContext('2d')
    const overlayCtx = overlayCanvas.getContext('2d')
    if (!baseCtx || !overlayCtx) return

    scrollTickRef.current()

    const currentLines = linesRef.current
    const currentScrollY = scrollYRef.current
    const currentSettings = settingsRef.current
    const currentTrackingMode = trackingModeRef.current
    const { width, height } = canvasSize

    if (width === 0 || height === 0) {
      rafRef.current = requestAnimationFrame(render)
      return
    }

    // --- Base layer: only redraw when scroll/layout/settings actually changed ---
    const settingsHash = `${currentSettings.theme}-${currentSettings.fontSize}-${currentSettings.fontFamily}`
    const needsBaseRedraw =
      lastBaseRenderRef.current.scrollY !== currentScrollY ||
      lastBaseRenderRef.current.linesLen !== currentLines.length ||
      lastBaseRenderRef.current.settingsHash !== settingsHash

    if (needsBaseRedraw) {
      renderBaseLayer(baseCtx, currentLines, currentSettings, currentScrollY, width, height)
      lastBaseRenderRef.current = {
        scrollY: currentScrollY,
        linesLen: currentLines.length,
        settingsHash,
      }
    }

    // --- Overlay layer: lightweight spotlight, redrawn every frame ---
    const rawGaze = gazeBridge.get()

    let fixationLine: number | null = null
    if (rawGaze.currentPoint && currentLines.length > 0) {
      const rect = baseCanvas.getBoundingClientRect()
      const gazeYInCanvas = rawGaze.currentPoint.y - rect.top
      // Apply vertical offset correction (webcam tracks below actual gaze)
      const correctedGazeY = gazeYInCanvas + GAZE_Y_OFFSET

      // One Euro Filter already smoothed the coordinates — direct lookup is stable
      const candidateLine = gazeToLine(correctedGazeY, currentScrollY, currentLines)
      fixationLine = candidateLine ?? fixationLineRef.current
    }
    fixationLineRef.current = fixationLine

    const renderGaze = { ...rawGaze, fixationLine }
    renderOverlay(overlayCtx, currentLines, currentSettings, renderGaze, currentScrollY, width, height)

    // Auto-scroll
    if (currentSettings.autoScrollEnabled && rawGaze.currentPoint && currentTrackingMode !== 'off') {
      const rect = baseCanvas.getBoundingClientRect()
      const gazeYRatio = (rawGaze.currentPoint.y - rect.top) / rect.height
      if (gazeYRatio > (1 - AUTO_SCROLL_ZONE) && rawGaze.isFixating) {
        scrollByRef.current(AUTO_SCROLL_SPEED)
      }
    }

    rafRef.current = requestAnimationFrame(render)
  }, [canvasSize])

  useEffect(() => {
    rafRef.current = requestAnimationFrame(render)
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
    }
  }, [render])

  // Click-to-correct: clicking on text feeds position to WebGazer as
  // additional calibration data, improving accuracy over time
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (trackingMode === 'webcam') {
      gazeTracker.recordCalibrationClick(e.clientX, e.clientY)
    }
  }, [trackingMode])

  // Wheel scroll
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      scrollBy(e.deltaY > 0 ? SCROLL_SPEED : -SCROLL_SPEED)
    },
    [scrollBy],
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          scrollBy(SCROLL_SPEED)
          break
        case 'ArrowUp':
          e.preventDefault()
          scrollBy(-SCROLL_SPEED)
          break
        case 'PageDown':
        case ' ':
          e.preventDefault()
          scrollBy(PAGE_SCROLL)
          break
        case 'PageUp':
          e.preventDefault()
          scrollBy(-PAGE_SCROLL)
          break
        case 'Home':
          e.preventDefault()
          setScrollY(0)
          break
        case 'End':
          e.preventDefault()
          setScrollY(getMaxScroll())
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [scrollBy, setScrollY, getMaxScroll])

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onClick={handleClick as any}
      onWheel={handleWheel as any}
    >
      {/* Base layer: stable text, never flickers */}
      <canvas
        ref={baseCanvasRef}
        tabIndex={0}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'block',
          outline: 'none',
        }}
      />
      {/* Overlay layer: spotlight effect only */}
      <canvas
        ref={overlayCanvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </div>
  )
}
